import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tour } from '@/types';
import { isGenericArtistName } from '@/utils/tourArtist';
import { isSeatPackage } from '@/utils/tourScoring';
import { isValidTourName as validateTourName } from '@/utils/tourValidation';

const normalize = (s: string) =>
  (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const isValidTour = (tourName: string, artistName: string, year: number): boolean => {
  const validation = validateTourName(tourName);
  if (!validation.valid) return false;
  if (isSeatPackage(tourName)) return false;
  const tourNameLower = normalize(tourName);
  const genericPattern = normalize(`${artistName} ${year}`);
  if (artistName && tourNameLower === genericPattern) return false;
  if (/^[\s,\-:+&]+/.test(tourName)) return false;
  return true;
};

async function fetchToursData(limit?: number): Promise<Tour[]> {
  let query = supabase
    .from('tours')
    .select('id, name, slug, artist_id, year, average_rating, concert_count, image, artists(name, slug)')
    .order('average_rating', { ascending: false });
  if (limit) query = query.limit(limit);
  const { data, error } = await query;

  if (error) throw error;

  const artistIds = Array.from(new Set((data || []).map((t: any) => t.artist_id).filter(Boolean)));
  const artistMap = new Map<string, { name: string; image?: string; genre?: string[]; slug?: string }>();
  
  if (artistIds.length) {
    const { data: artistRows } = await supabase
      .from('artists')
      .select('id, name, image, genre, slug')
      .in('id', artistIds as string[]);
    (artistRows || []).forEach((a: any) => artistMap.set(a.id, { name: a.name, image: a.image, genre: a.genre || [], slug: a.slug || undefined }));
  }

  const tourIds = (data || []).map((t: any) => t.id);

  // Chunk large .in() queries to prevent 400 errors from oversized URLs
  const CHUNK_SIZE = 150;
  const chunks: string[][] = [];
  for (let i = 0; i < tourIds.length; i += CHUNK_SIZE) {
    chunks.push(tourIds.slice(i, i + CHUNK_SIZE));
  }

  const allReviews: any[] = [];
  const allLikes: any[] = [];
  const allShowDates: any[] = [];

  await Promise.all(chunks.map(async (chunk) => {
    const [reviewResult, likeResult, showDatesResult] = await Promise.all([
      supabase.from('reviews').select('tour_id, rating').in('tour_id', chunk),
      supabase.from('user_likes').select('item_id').eq('item_type', 'tour').in('item_id', chunk),
      supabase.from('tour_show_dates').select('tour_id, nearest_show_date, latest_show_date').in('tour_id', chunk),
    ]);
    allReviews.push(...(reviewResult.data || []));
    allLikes.push(...(likeResult.data || []));
    allShowDates.push(...(showDatesResult.data || []));
  }));

  const tourLikeCounts = new Map<string, number>();
  allLikes.forEach((l: any) => {
    if (l.item_id) {
      tourLikeCounts.set(l.item_id, (tourLikeCounts.get(l.item_id) || 0) + 1);
    }
  });

  const tourStats = new Map<string, { count: number; avgRating: number }>();
  allReviews.forEach((r: any) => {
    if (!r.tour_id) return;
    const existing = tourStats.get(r.tour_id) || { count: 0, avgRating: 0 };
    const newCount = existing.count + 1;
    const newAvg = (existing.avgRating * existing.count + Number(r.rating)) / newCount;
    tourStats.set(r.tour_id, { count: newCount, avgRating: newAvg });
  });

  const tourDates = new Map<string, { nearest?: string; latest?: string }>();
  allShowDates.forEach((d: any) => {
    if (d.tour_id) {
      tourDates.set(d.tour_id, { nearest: d.nearest_show_date, latest: d.latest_show_date });
    }
  });

  return data?.filter((tour: any) => {
    const artistData = artistMap.get(tour.artist_id);
    const artistName = artistData?.name || (tour.artists as any)?.name || '';
    if (isGenericArtistName(artistName)) return false;
    return isValidTour(tour.name, artistName, tour.year);
  }).map((tour: any) => {
    const stats = tourStats.get(tour.id);
    const artistData = artistMap.get(tour.artist_id);
    const artistName = artistData?.name || (tour.artists as any)?.name || '';
    const dates = tourDates.get(tour.id);
    return {
      id: tour.id,
      slug: (tour as any).slug || undefined,
      name: tour.name,
      artistId: tour.artist_id,
      artistSlug: artistData?.slug || (tour.artists as any)?.slug || undefined,
      artistName: artistName,
      year: tour.year,
      averageRating: stats ? stats.avgRating : 0,
      concertCount: tour.concert_count || 0,
      image: tour.image,
      reviewCount: stats ? stats.count : 0,
      artistImage: artistData?.image,
      nearestShowDate: dates?.nearest,
      latestShowDate: dates?.latest,
      likeCount: tourLikeCounts.get(tour.id) || 0,
      artistGenre: artistData?.genre || [],
    };
  }) || [];
}

export const useTours = (enabled: boolean = true, limit?: number) => {
  const { data: tours = [], isLoading: loading, error, refetch } = useQuery({
    queryKey: ['tours', 'list', limit ?? 'all'],
    queryFn: () => fetchToursData(limit),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  return { tours, loading, error: error?.message || null, refetch };
};

// Extended tour type for Ticketmaster data
interface TicketmasterTour extends Tour {
  shows?: Array<{
    id: string;
    name: string;
    date: string;
    venue: string;
    city: string;
    country: string;
    state?: string;
    latitude?: number;
    longitude?: number;
  }>;
  venues?: Array<{
    name: string;
    city: string;
    country: string;
    latitude: number;
    longitude: number;
    date: string;
  }>;
  artistName?: string;
}

async function fetchSingleTour(idOrSlug: string): Promise<TicketmasterTour | null> {
  const isUUID = /^[0-9a-fA-F-]{36}$/.test(idOrSlug);
  // Ticketmaster-sourced tour ids look like "tm-<eventId>-tour".
  const isTMPrefixed = /^tm-/.test(idOrSlug);
  const isTM = isTMPrefixed || (!isUUID && /^[A-Za-z0-9]{10,40}$/.test(idOrSlug) && !idOrSlug.includes('-'));

  if (isTMPrefixed) return fetchTicketmasterTour(idOrSlug);

  if (isUUID || !isTM) {
    const q = supabase.from('tours').select('*');
    const { data, error } = await (isUUID
      ? q.eq('id', idOrSlug).maybeSingle()
      : q.eq('slug', idOrSlug).maybeSingle());

    if (error) throw error;
    if (!data) {
      // Fall back to TM lookup if it looks like a TM id
      if (isTM) return fetchTicketmasterTour(idOrSlug);
      return null;
    }

    const { data: reviewData } = await supabase
      .from('reviews')
      .select('rating')
      .eq('tour_id', data.id);

    let avgRating = 0;
    let reviewCount = 0;
    if (reviewData && reviewData.length > 0) {
      const sum = reviewData.reduce((acc, r) => acc + Number(r.rating), 0);
      avgRating = sum / reviewData.length;
      reviewCount = reviewData.length;
    }

    return {
      id: data.id,
      name: data.name,
      artistId: data.artist_id,
      year: data.year,
      averageRating: avgRating,
      concertCount: data.concert_count || 0,
      image: data.image,
      reviewCount,
      slug: (data as any).slug || undefined,
    } as TicketmasterTour;
  }

  return fetchTicketmasterTour(idOrSlug);
}

async function fetchTicketmasterTour(tourId: string): Promise<TicketmasterTour | null> {
  console.log('📡 Fetching Ticketmaster tour details for ID:', tourId);
  const { data: tmData, error: tmError } = await supabase.functions.invoke('ticketmaster-tours', {
    body: { eventId: tourId }
  });
  if (tmError) { console.error('Ticketmaster fetch error:', tmError); return null; }
  if (tmData?.tour) {
    const tmTour = tmData.tour;
    return {
      id: tmTour.id,
      name: tmTour.name,
      artistId: tmTour.artistId || '',
      year: tmTour.year || new Date().getFullYear(),
      averageRating: 0,
      concertCount: tmTour.concertCount || 0,
      image: tmTour.image,
      reviewCount: 0,
      shows: tmTour.shows || [],
      venues: tmTour.venues || [],
      artistName: tmTour.artistName,
    };
  }
  return null;
}

export const useTour = (idOrSlug: string) => {
  const { data: tour = null, isLoading: loading, error, refetch } = useQuery({
    queryKey: ['tours', idOrSlug, 'details'],
    queryFn: () => fetchSingleTour(idOrSlug),
    enabled: !!idOrSlug,
    staleTime: 5 * 60 * 1000,
  });

  return { tour, loading, error: error?.message || null, refetch };
};

async function fetchToursByArtistData(artistId: string): Promise<Tour[]> {
  const { data, error } = await supabase
    .from('tours')
    .select('*, artists(name)')
    .eq('artist_id', artistId)
    .order('year', { ascending: false });

  if (error) throw error;

  let artistNameDb = '';
  try {
    const { data: artistRow } = await supabase
      .from('artists')
      .select('name')
      .eq('id', artistId)
      .maybeSingle();
    artistNameDb = artistRow?.name || '';
  } catch {}

  const filteredData = data?.filter((t: any) => {
    const artistName = (t.artists as any)?.name || artistNameDb || '';
    if (isGenericArtistName(artistName)) return false;
    return isValidTour(t.name, artistName, t.year);
  }) || [];

  const normalizeName = (name: string) =>
    name.toLowerCase().replace(/tour/g, '').replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();

  const map = new Map<string, Tour>();
  for (const t of filteredData) {
    const key = `${t.artist_id}-${t.year}-${normalizeName(t.name)}`;
    const candidate: Tour = {
      id: t.id,
      slug: t.slug || undefined,
      name: t.name,
      artistId: t.artist_id,
      year: t.year,
      averageRating: Number(t.average_rating) || 0,
      concertCount: t.concert_count || 0,
      image: t.image,
    };
    const existing = map.get(key);
    if (!existing) {
      map.set(key, candidate);
    } else if (
      candidate.concertCount > existing.concertCount ||
      (candidate.concertCount === existing.concertCount && candidate.averageRating > existing.averageRating)
    ) {
      map.set(key, candidate);
    }
  }

  return Array.from(map.values()).sort(
    (a, b) => b.year - a.year || b.concertCount - a.concertCount || b.averageRating - a.averageRating
  );
}

export const useToursByArtist = (artistId: string) => {
  const { data: tours = [], isLoading: loading, error, refetch } = useQuery({
    queryKey: ['tours', 'artist', artistId],
    queryFn: () => fetchToursByArtistData(artistId),
    enabled: !!artistId,
    staleTime: 5 * 60 * 1000,
  });

  return { tours, loading, error: error?.message || null, refetch };
};
